import { useMemo } from "react";
import { Box, Typography, Divider, CircularProgress, useMediaQuery } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { Check } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { useTranslation } from "../../../../language";

const TOP_N = 10;
const NAMED_SLICES = 7;

const CATEGORICAL_LIGHT = ["#2a78d6", "#1baf7a", "#eda100", "#008300", "#4a3aa7", "#e34948", "#e87ba4"];
const CATEGORICAL_DARK  = ["#3987e5", "#199e70", "#c98500", "#008300", "#9085e9", "#e66767", "#d55181"];

function evalColor(evaluation, theme) {
  return evaluation === "N" ? theme.palette.traffic.green : theme.palette.traffic.orange;
}

const RADIAN = Math.PI / 180;

function renderNameLabel(nameColor, fontSize = 13) {
  return ({ cx, cy, midAngle, outerRadius, name }) => {
    const radius = outerRadius + (fontSize < 13 ? 16 : 22);
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const anchor = x > cx ? "start" : "end";
    return (
      <text x={x} y={y} textAnchor={anchor} dominantBaseline="central" fontSize={fontSize} fontWeight={500} fill={nameColor}>
        {name}
      </text>
    );
  };
}

export default function GutBacteriaComposition({ records, loading, taxonomySegments, levelLabel }) {
  const theme = useTheme();
  const { t } = useTranslation();
  // Radiating name labels have less room to spread out on narrow screens —
  // shrinking the donut itself (rather than just the container) keeps them
  // from overlapping, matching how the chart already reads on desktop.
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const isLight = theme.palette.mode === "light";
  const colors = isLight ? CATEGORICAL_LIGHT : CATEGORICAL_DARK;
  const otherColor = isLight ? theme.palette.grey.C400 : theme.palette.greydark.C400;
  const cardBg = isLight ? theme.palette.grey.C50 : theme.palette.greydark.C900;

  const pieData = useMemo(() => {
    const top = records
      .filter(r => r.category === "all_genus" && typeof r.taxonomy === "string" && r.taxonomy.split(";").length === taxonomySegments)
      .map(r => ({
        name: r.genus || r.phylum || r.taxonomy,
        value: Number(r.frequency) || 0,
        referenceMin: r.referenceMin,
        referenceMax: r.referenceMax,
        evaluation: r.evaluation,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, TOP_N);

    const named = top.slice(0, NAMED_SLICES).map((item, i) => ({ ...item, fill: colors[i % colors.length] }));
    const rest = top.slice(NAMED_SLICES);
    const otherValue = rest.reduce((sum, item) => sum + item.value, 0);
    if (otherValue > 0) {
      named.push({ name: t("gut_composition_other") || "Other", value: otherValue, fill: otherColor });
    }
    return named;
  }, [records, taxonomySegments, colors, otherColor, t]);

  // Only the named picks carry a real evaluation/reference range — the "Other" bucket is an aggregate.
  const tableItems = useMemo(() => pieData.filter(item => item.evaluation != null), [pieData]);

  // Reserve enough side margin for the longest label so it never clips — data-driven,
  // since genus/family names vary a lot in length across patients and taxonomy levels.
  // Capped much tighter on xs: at up to 240px per side, the two margins alone could
  // exceed a mobile viewport's width, leaving zero/negative room for the pie itself
  // and collapsing it to a single point (labels with no visible ring).
  const labelMargin = useMemo(() => {
    const longest = pieData.reduce((max, item) => Math.max(max, item.name.length), 0);
    return isXs
      ? Math.min(70, Math.max(50, longest * 3 + 20))
      : Math.min(240, Math.max(90, longest * 7 + 26));
  }, [pieData, isXs]);

  if (loading) {
    return <Box textAlign="center" my={4}><CircularProgress size={28} /></Box>;
  }

  if (!pieData.length) return null;

  return (
    <Box>
      <Typography variant="body1" fontWeight={700} sx={{ color: isLight ? "greydark.C900" : "grey.C10", mt: 4, mb: 1, whiteSpace: "nowrap" }}>
        {t("gut_composition_title") || "Composition of Your Gut Flora"}{levelLabel ? ` - ${levelLabel}` : ""}
      </Typography>

      {/* ── Pie chart + top picks table ──────────────────────────────────── */}
      <Box sx={{
        p: 2, borderRadius: 4, overflow: "hidden",
        bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
        backdropFilter: "blur(28px) saturate(180%)",
        WebkitBackdropFilter: "blur(28px) saturate(180%)",
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
        boxShadow: theme.palette.mode === "dark"
          ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
          : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
      }}>
        <Box sx={{ width: "100%", height: { xs: 320, sm: 380 }, mx: "auto" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 12, right: labelMargin, bottom: 12, left: labelMargin }}>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                innerRadius={isXs ? "38%" : "55%"}
                outerRadius={isXs ? "62%" : "90%"}
                paddingAngle={2}
                strokeWidth={2}
                stroke={cardBg}
                label={renderNameLabel(theme.palette.text.primary, isXs ? 10 : 13)}
                labelLine={{ stroke: theme.palette.text.disabled, strokeWidth: 1 }}
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Pie>
              <RechartsTooltip
                formatter={(value, name) => [`${Number(value).toFixed(2)} %`, name]}
                contentStyle={{
                  borderRadius: 10,
                  border: `1px solid ${isLight ? theme.palette.grey.C900 : theme.palette.greydark.C400}`,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* ── Top picks table ───────────────────────────────────────────── */}
        {tableItems.length > 0 && (
          <>
            <Divider sx={{ mt: 1, mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1, px: 0.5, textTransform: "uppercase", letterSpacing: 0.4 }}>
              {t("gut_composition_table_title") || "Top Genus Picks"}
            </Typography>
            <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
              {tableItems.map((item, i) => (
                <Box key={item.name} sx={{
                  bgcolor: i % 2 === 0
                    ? (isLight ? "grey.C50"  : "greydark.C900")
                    : (isLight ? "grey.C100" : "greydark.C800"),
                }}>
                  <Box sx={{ px: 2, py: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: evalColor(item.evaluation, theme) }} />
                      <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xl: "1rem" } }}>{item.name}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", pl: "16px", mt: 0.75 }}>
                      <Typography variant="body1" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {item.value.toFixed(2)} %
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      {item.evaluation === "N"
                        ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                        : (
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: evalColor(item.evaluation, theme) }}>
                            {item.evaluation === "H" ? (t("bt_status_high") || "HIGH") : item.evaluation === "L" ? (t("bt_status_low") || "LOW") : "ABNORMAL"}
                          </Typography>
                        )
                      }
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", pl: "16px", mt: 0.5 }}>
                      Ref: {item.referenceMin} – {item.referenceMax}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
